import { isPartnerEligibleForSimplrCutover } from "@expert/shared-types";

export interface SelectWebsocketUrlArgs {
    /** Resolved value of the `isSimplrWebsocketLiveEnabled` feature flag. */
    isSimplrWebsocketLiveEnabled: boolean;
    /** Default GAIA websocket base URL. */
    gaiaWebsocketUrl: string;
    /** Simplr websocket base URL, or `undefined` when not configured for this environment. */
    simplrWebsocketUrl: string | undefined;
    /** Partner of the active call, used as a client-side backstop independent of the flag. */
    partner: string | undefined;
    /**
     * Whether this is a production build. In production the backstop restricts the live cutover
     * to the Simplr-eligible partner allowlist; outside production any partner is eligible, so
     * the cutover can be exercised locally without needing an eligible call.
     */
    isProdMode: boolean;
}

/**
 * Chooses the live realtime websocket base URL for the current call.
 *
 * The Simplr websocket stands in for GAIA during the GAIA -> LogicBuilder
 * migration. A call is routed there only when the `isSimplrWebsocketLiveEnabled`
 * feature flag is on, a Simplr URL is actually configured, AND the active call's
 * partner is eligible for the cutover (see {@link isPartnerEligibleForSimplrCutover});
 * otherwise it falls back to the default GAIA websocket.
 *
 * The partner check here does not just trust the flag's own partner targeting: the flag is
 * fetched asynchronously and can briefly reflect a previous call's partner while switching
 * calls, so this is a synchronous backstop against sending an ineligible call to Simplr.
 * The flag re-resolves as the expert switches calls, and `react-use-websocket` reconnects
 * when the URL changes.
 */
export function selectWebsocketUrl({
    isSimplrWebsocketLiveEnabled,
    gaiaWebsocketUrl,
    simplrWebsocketUrl,
    partner,
    isProdMode,
}: SelectWebsocketUrlArgs): string {
    if (isSimplrWebsocketLiveEnabled && simplrWebsocketUrl && isPartnerEligibleForSimplrCutover(partner, isProdMode)) {
        return simplrWebsocketUrl;
    }
    return gaiaWebsocketUrl;
}
